import { z } from 'zod';
import { caseCategorySchema } from './case';
import { localizedTextSchema } from './localized';

export const PUBLIC_IMAGE_WIDTHS = [480, 1000, 2000] as const;

const publicDocumentSchema = z.record(z.string(), z.unknown());
const publicTranslationEntrySchema = z.object({
  block: publicDocumentSchema,
  sourceHash: z.string().min(1),
});

export const publicRichContentSchema = z.object({
  master: publicDocumentSchema,
  sourceLanguage: z.enum(['nl', 'fr', 'en']),
  translations: z
    .object({
      nl: z.record(z.string(), publicTranslationEntrySchema).optional(),
      fr: z.record(z.string(), publicTranslationEntrySchema).optional(),
      en: z.record(z.string(), publicTranslationEntrySchema).optional(),
    })
    .optional(),
});

export const publicMediaAssetSchema = z.object({
  id: z.uuid(),
  originalFilename: z.string().min(1),
  alt: localizedTextSchema.nullable(),
  mimeType: z.string().min(1),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
});

export const publicMediaSchema = z.object({
  role: z.enum(['hero', 'gallery', 'content']),
  cropX: z.number().int().min(0).max(100),
  mediaAsset: publicMediaAssetSchema,
});

export const publicCaseListItemSchema = z.object({
  slug: z.string().min(1),
  category: caseCategorySchema,
  title: localizedTextSchema,
  tagline: localizedTextSchema.nullable(),
  publishedAt: z.string().datetime(),
  image: publicMediaSchema.nullable(),
});

export const publicCaseDetailSchema = z.object({
  slug: z.string().min(1),
  category: caseCategorySchema,
  title: localizedTextSchema,
  tagline: localizedTextSchema.nullable(),
  subtitle: localizedTextSchema.nullable(),
  content: publicRichContentSchema.nullable(),
  location: localizedTextSchema.nullable(),
  attendees: localizedTextSchema.nullable(),
  company: localizedTextSchema.nullable(),
  duration: localizedTextSchema.nullable(),
  publishedAt: z.string().datetime(),
  media: z.array(publicMediaSchema),
});

export const publicBlogListItemSchema = z.object({
  slug: z.string().min(1),
  title: localizedTextSchema,
  excerpt: localizedTextSchema.nullable(),
  publishedAt: z.string().datetime(),
  image: publicMediaSchema.nullable(),
});

export const publicBlogDetailSchema = z.object({
  slug: z.string().min(1),
  title: localizedTextSchema,
  excerpt: localizedTextSchema.nullable(),
  content: publicRichContentSchema.nullable(),
  seoDescription: localizedTextSchema.nullable(),
  publishedAt: z.string().datetime(),
  media: z.array(publicMediaSchema),
});

export const publicSitemapEntrySchema = z.object({
  slug: z.string().min(1),
  lastModified: z.string().datetime().optional(),
});

export const publicSitemapEntriesSchema = z.array(publicSitemapEntrySchema);

export const publicCaseListSchema = z.array(publicCaseListItemSchema);

export const publicBlogListSchema = z.object({
  items: z.array(publicBlogListItemSchema),
  page: z.number().int().min(1),
  perPage: z.number().int().min(1),
  total: z.number().int().min(0),
  totalPages: z.number().int().min(0),
});

export type PublicCaseListItem = z.infer<typeof publicCaseListItemSchema>;
export type PublicCaseDetail = z.infer<typeof publicCaseDetailSchema>;
export type PublicBlogListItem = z.infer<typeof publicBlogListItemSchema>;
export type PublicBlogDetail = z.infer<typeof publicBlogDetailSchema>;
export type PublicCaseList = z.infer<typeof publicCaseListSchema>;
export type PublicBlogList = z.infer<typeof publicBlogListSchema>;
export type PublicSitemapEntry = z.infer<typeof publicSitemapEntrySchema>;
